import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import {
  getFacturas,
  searchFacturas,
  getProveedores,
  getCategorias,
  batchValidarFacturas,
  deleteFactura,
} from '../services/api';
import SearchBar from '../components/SearchBar';
import FilterPanel from '../components/FilterPanel';
import FacturaTable from '../components/FacturaTable';
import ExportButton from '../components/ExportButton';
import toast from 'react-hot-toast';

export default function FacturasList() {
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('fecha_factura');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const pageSize = 25;

  useEffect(() => {
    Promise.all([
      getProveedores().catch(() => []),
      getCategorias().catch(() => []),
    ]).then(([p, c]) => {
      setProveedores(Array.isArray(p) ? p : []);
      setCategorias(Array.isArray(c) ? c : []);
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let result;
      if (searchQuery.trim()) {
        result = await searchFacturas(searchQuery);
        if (Array.isArray(result)) {
          result = { items: result, total: result.length };
        }
      } else {
        const params = {
          ...filters,
          order_by: sortBy,
          order_dir: sortDir,
          page,
          per_page: pageSize,
          estado: filters.estados,
          categoria: filters.categorias,
        };
        delete params.estados;
        delete params.categorias;
        result = await getFacturas(params);
        if (Array.isArray(result)) {
          result = { items: result, total: result.length };
        }
      }
      const items = result.items || result.facturas || [];
      setFacturas(items);
      setTotal(result.total || items.length);
      setTotalPages(Math.max(1, Math.ceil((result.total || items.length) / pageSize)));
    } catch (err) {
      toast.error('Error cargando facturas: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, sortBy, sortDir, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [searchQuery, filters]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === facturas.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(facturas.map((f) => f.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBulkValidate = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    try {
      await batchValidarFacturas(selectedIds);
      toast.success(`${count} factura${count !== 1 ? 's' : ''} validada${count !== 1 ? 's' : ''}`);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error('Error validando facturas: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    if (!window.confirm(`¿Eliminar ${count} factura${count !== 1 ? 's' : ''}?`)) return;
    try {
      await Promise.all(selectedIds.map((id) => deleteFactura(id)));
      toast.success(`${count} factura${count !== 1 ? 's' : ''} eliminada${count !== 1 ? 's' : ''}`);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error('Error eliminando facturas: ' + err.message);
    }
  };

  const buildExportFilters = () => {
    const params = { ...filters };
    if (searchQuery.trim()) params.q = searchQuery;
    return params;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Facturas</h1>
        <ExportButton filters={buildExportFilters()} />
      </div>

      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Buscar por proveedor, n factura, CIF..."
      />

      <FilterPanel
        filters={filters}
        onChange={setFilters}
        proveedores={proveedores}
        categorias={categorias}
      />

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
          <span className="text-sm text-primary-700 font-medium">
            {selectedIds.length} seleccionada{selectedIds.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={handleBulkValidate}
            className="flex items-center gap-1 text-sm text-green-700 hover:text-green-800 font-medium"
          >
            <CheckCircle className="w-4 h-4" />
            Validar
          </button>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium"
          >
            <Trash2 className="w-4 h-4" />
            Eliminar
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <FacturaTable
            facturas={facturas}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onRowClick={(id) => navigate(`/facturas/${id}`)}
          />
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {total} factura{total !== 1 ? 's' : ''} en total
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="btn-secondary p-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 px-2">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="btn-secondary p-2"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
